<script setup lang="ts">
// Enable SSR for faster initial page load
// Data fetching happens client-side with useLazyFetch
definePageMeta({
  ssr: true
});

useHead({
  title: "Komunite - Dashboard"
});

// Time range options
const timeRangeOptions = [
  { label: "7 days", value: 7 },
  { label: "14 days", value: 14 },
  { label: "30 days", value: 30 },
  { label: "90 days", value: 90 },
  { label: "All time", value: 36500 } // ~100 years
];

// Selected time range
const selectedTimeRange = ref(30);

// Fetch dashboard stats with reactive time range
const { data: stats } = useLazyFetch("/api/dashboard/overview", {
  query: computed(() => ({ days: selectedTimeRange.value })),
  watch: [selectedTimeRange]
});

// Chart granularity options
const chartGranularityOptions = [
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" }
];

// Selected chart granularity
const selectedChartGranularity = ref("daily");

// Fetch chart data with reactive granularity
const { data: chartData, pending: chartPending } = useLazyFetch("/api/dashboard/trends", {
  query: computed(() => ({ granularity: selectedChartGranularity.value })),
  watch: [selectedChartGranularity]
});

// Format number with thousand separators
const formatNumber = (num: number) => {
  return new Intl.NumberFormat("tr-TR").format(num);
};

// Selected time range for leaderboards
const selectedLeaderboardTimeRange = ref(30);

// Customers state
const customersIncludeStaff = ref(false);
const customersSortBy = ref("total_spent");

// Staff state
const staffSortBy = ref("total_revenue");

// Fetch top customers with reactive parameters
const { data: topCustomers } = useLazyFetch("/api/dashboard/leaderboards-customers", {
  query: computed(() => ({
    days: selectedLeaderboardTimeRange.value,
    include_staff: customersIncludeStaff.value.toString(),
    sort_by: customersSortBy.value
  })),
  watch: [selectedLeaderboardTimeRange, customersIncludeStaff, customersSortBy]
});

// Fetch top staff with reactive parameters
const { data: topStaff } = useLazyFetch("/api/dashboard/leaderboards-staff", {
  query: computed(() => ({
    days: selectedLeaderboardTimeRange.value,
    sort_by: staffSortBy.value
  })),
  watch: [selectedLeaderboardTimeRange, staffSortBy]
});

// Leaderboard column configurations
const customersColumns = [
  { key: "name", label: "NAME", sortable: false, align: "left" as const, width: "31%" },
  { key: "visit_count", label: "VISITS", sortable: true, format: "number" as const, align: "right" as const, width: "23%" },
  { key: "avg_bill", label: "AVG. BILL (TL)", sortable: true, format: "currency" as const, align: "right" as const, width: "23%" },
  { key: "total_spent", label: "SPENT (TL)", sortable: true, format: "currency" as const, align: "right" as const, width: "23%" }
];

const staffColumns = [
  { key: "name", label: "NAME", sortable: false, align: "left" as const, width: "31%" },
  { key: "orders_served", label: "SERVICES", sortable: true, format: "number" as const, align: "right" as const, width: "23%" },
  { key: "new_members", label: "SIGNUPS", sortable: true, format: "number" as const, align: "right" as const, width: "23%" },
  { key: "total_revenue", label: "REVENUE (TL)", sortable: true, format: "currency" as const, align: "right" as const, width: "23%" }
];

// Handler functions
const handleCustomersSortChange = (sortBy: string) => {
  customersSortBy.value = sortBy;
};

const handleCustomersIncludeStaffChange = (value: boolean) => {
  customersIncludeStaff.value = value;
};

const handleStaffSortChange = (sortBy: string) => {
  staffSortBy.value = sortBy;
};
</script>

<template>
  <UDashboardPanel id="dashboard">
    <template #body>
      <div class="p-6 space-y-12">
        <!-- Overview Section -->
        <AppSection title="Overview">
          <template #controller>
            <USelectMenu
              v-model="selectedTimeRange"
              :items="timeRangeOptions"
              value-key="value"
              :search-input="false"
              placeholder="Select time range"
              icon="i-lucide-calendar-clock"
              trailing-icon="i-lucide-chevron-down"
              variant="outline"
              size="sm"
            />
          </template>

          <div class="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <DashboardBarMetricCard
              title="New Customers"
              icon="i-lucide-user-round-plus"
              :total="stats?.customers.total ?? 0"
              :by-branch="stats?.customers.byBranch ?? {}"
              :formatter="formatNumber"
            />

            <DashboardBarMetricCard
              title="Orders Count"
              icon="i-lucide-soup"
              :total="(stats?.orders.total as number) ?? 0"
              :by-branch="stats?.orders.byBranch ?? {}"
              :formatter="formatNumber"
            />

            <DashboardBarMetricCard
              title="Average Bill"
              icon="i-lucide-wallet"
              :total="stats?.avgOrderPrice.overall ?? 0"
              :by-branch="stats?.avgOrderPrice.byBranch ?? {}"
              :formatter="formatTurkishLira"
            />

            <DashboardBarMetricCard
              title="Total Revenue"
              icon="i-lucide-gem"
              :total="(stats?.revenue.total as number) ?? 0"
              :by-branch="stats?.revenue.byBranch ?? {}"
              :formatter="formatTurkishLira"
            />
          </div>
        </AppSection>

        <!-- Trends Section -->
        <AppSection title="Trends">
          <template #controller>
            <USelectMenu
              v-model="selectedChartGranularity"
              :items="chartGranularityOptions"
              value-key="value"
              :search-input="false"
              placeholder="Select granularity"
              icon="i-lucide-bar-chart"
              trailing-icon="i-lucide-chevron-down"
              variant="outline"
              size="sm"
            />
          </template>

          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <DashboardTrendCard
              chart-type="bar"
              data-field="newCustomers"
              title="New Customers"
              icon="i-lucide-user-round-plus"
              :daily-data="chartData?.data || []"
              :granularity="selectedChartGranularity"
              :loading="chartPending"
            />

            <DashboardTrendCard
              chart-type="bar"
              data-field="orders"
              title="Orders Count"
              icon="i-lucide-soup"
              :daily-data="chartData?.data || []"
              :granularity="selectedChartGranularity"
              :loading="chartPending"
            />

            <DashboardTrendCard
              chart-type="line"
              data-field="avgBill"
              title="Average Bill"
              icon="i-lucide-wallet"
              :daily-data="chartData?.data || []"
              :granularity="selectedChartGranularity"
              :loading="chartPending"
            />

            <DashboardTrendCard
              chart-type="bar"
              data-field="revenue"
              title="Total Revenue"
              icon="i-lucide-gem"
              :daily-data="chartData?.data || []"
              :granularity="selectedChartGranularity"
              :loading="chartPending"
            />
          </div>
        </AppSection>

        <!-- Leaderboards Section -->
        <AppSection title="Leaderboards">
          <template #controller>
            <USelectMenu
              v-model="selectedLeaderboardTimeRange"
              :items="timeRangeOptions"
              value-key="value"
              :search-input="false"
              placeholder="Select time range"
              icon="i-lucide-calendar-clock"
              trailing-icon="i-lucide-chevron-down"
              variant="outline"
              size="sm"
            />
          </template>

          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <DashboardLeaderboardCard
              title="Top Customers"
              icon="i-lucide-user-star"
              :columns="customersColumns"
              :data="topCustomers || []"
              :sort-by="customersSortBy"
              :show-include-staff="true"
              :include-staff="customersIncludeStaff"
              @sort-change="handleCustomersSortChange"
              @include-staff-change="handleCustomersIncludeStaffChange"
            />

            <DashboardLeaderboardCard
              title="Top Staff"
              icon="i-lucide-hand-platter"
              :columns="staffColumns"
              :data="topStaff || []"
              :sort-by="staffSortBy"
              @sort-change="handleStaffSortChange"
            />
          </div>
        </AppSection>
      </div>
    </template>
  </UDashboardPanel>
</template>
